import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaTimes, FaWhatsapp } from "react-icons/fa";
import { EVENT_TYPE_OPTIONS } from "../constants/siteContact";
import { trackEvent } from "../utils/analytics";
import {
  buildDefaultWhatsAppUrl,
  buildInquiryWhatsAppUrl,
  formatInquiryMessage,
} from "../utils/whatsappMessage";
import "./WhatsAppAssistant.css";

const STEPS = {
  WELCOME: "welcome",
  EVENT_TYPE: "eventType",
  EVENT_DATE: "eventDate",
  GUEST_COUNT: "guestCount",
  NAME: "name",
  PHONE: "phone",
  DETAILS: "details",
  SUMMARY: "summary",
};

const INITIAL_FORM = {
  eventType: "",
  eventDate: "",
  guestCount: "",
  name: "",
  phone: "",
  details: "",
};

const WhatsAppAssistant = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(STEPS.WELCOME);
  const [form, setForm] = useState(INITIAL_FORM);
  const panelRef = useRef(null);

  const reset = useCallback(() => {
    setStep(STEPS.WELCOME);
    setForm(INITIAL_FORM);
  }, []);

  const close = useCallback(() => {
    if (open && step !== STEPS.WELCOME && step !== STEPS.SUMMARY) {
      trackEvent("whatsapp_assistant_abandon", { step });
    }
    setOpen(false);
    reset();
  }, [open, reset, step]);

  const openPanel = () => {
    setOpen(true);
    trackEvent("whatsapp_assistant_open");
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openWhatsApp = (url, complete = false) => {
    if (complete) {
      trackEvent("whatsapp_assistant_complete");
    }
    window.open(url, "_blank", "noopener,noreferrer");
    close();
  };

  const handleDirectWhatsApp = () => {
    openWhatsApp(buildDefaultWhatsAppUrl());
  };

  const handleSubmitInquiry = () => {
    openWhatsApp(buildInquiryWhatsAppUrl(form), true);
  };

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !e.target.closest(".whatsapp-assistant-fab")
      ) {
        close();
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, close]);

  return (
    <div className="whatsapp-assistant-root" aria-live="polite">
      {open ? (
        <div
          ref={panelRef}
          className="whatsapp-assistant-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="whatsapp-assistant-title"
        >
          <header className="whatsapp-assistant-header">
            <div className="whatsapp-assistant-header-text">
              <FaWhatsapp className="whatsapp-assistant-header-icon" aria-hidden />
              <div>
                <h2 id="whatsapp-assistant-title">Panthi Events</h2>
                <p>We typically reply on WhatsApp</p>
              </div>
            </div>
            <button
              type="button"
              className="whatsapp-assistant-close"
              onClick={close}
              aria-label="Close chat"
            >
              <FaTimes aria-hidden />
            </button>
          </header>

          <div className="whatsapp-assistant-body">
            {step === STEPS.WELCOME && (
              <div className="whatsapp-assistant-step">
                <p className="whatsapp-assistant-lead">
                  Hi! Tell us about your event and we&apos;ll connect you with
                  our team on WhatsApp.
                </p>
                <button
                  type="button"
                  className="whatsapp-assistant-btn whatsapp-assistant-btn-primary"
                  onClick={() => setStep(STEPS.EVENT_TYPE)}
                >
                  Start inquiry
                </button>
                <button
                  type="button"
                  className="whatsapp-assistant-link"
                  onClick={handleDirectWhatsApp}
                >
                  Chat directly on WhatsApp
                </button>
              </div>
            )}

            {step === STEPS.EVENT_TYPE && (
              <div className="whatsapp-assistant-step">
                <p className="whatsapp-assistant-question">
                  What type of event are you planning?
                </p>
                <div className="whatsapp-assistant-options">
                  {EVENT_TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`whatsapp-assistant-option${
                        form.eventType === opt.value ? " is-selected" : ""
                      }`}
                      onClick={() => {
                        updateField("eventType", opt.value);
                        setStep(STEPS.EVENT_DATE);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === STEPS.EVENT_DATE && (
              <div className="whatsapp-assistant-step">
                <label className="whatsapp-assistant-label" htmlFor="wa-event-date">
                  When is your event? <span className="wa-optional">(optional)</span>
                </label>
                <input
                  id="wa-event-date"
                  type="text"
                  className="whatsapp-assistant-input"
                  placeholder="e.g. March 2026"
                  value={form.eventDate}
                  onChange={(e) => updateField("eventDate", e.target.value)}
                />
                <div className="whatsapp-assistant-nav">
                  <button
                    type="button"
                    className="whatsapp-assistant-btn whatsapp-assistant-btn-secondary"
                    onClick={() => setStep(STEPS.EVENT_TYPE)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="whatsapp-assistant-btn whatsapp-assistant-btn-primary"
                    onClick={() => setStep(STEPS.GUEST_COUNT)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {step === STEPS.GUEST_COUNT && (
              <div className="whatsapp-assistant-step">
                <label className="whatsapp-assistant-label" htmlFor="wa-guests">
                  Approx. guest count? <span className="wa-optional">(optional)</span>
                </label>
                <input
                  id="wa-guests"
                  type="text"
                  className="whatsapp-assistant-input"
                  placeholder="e.g. 200–300"
                  value={form.guestCount}
                  onChange={(e) => updateField("guestCount", e.target.value)}
                />
                <div className="whatsapp-assistant-nav">
                  <button
                    type="button"
                    className="whatsapp-assistant-btn whatsapp-assistant-btn-secondary"
                    onClick={() => setStep(STEPS.EVENT_DATE)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="whatsapp-assistant-btn whatsapp-assistant-btn-primary"
                    onClick={() => setStep(STEPS.NAME)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {step === STEPS.NAME && (
              <div className="whatsapp-assistant-step">
                <label className="whatsapp-assistant-label" htmlFor="wa-name">
                  Your name
                </label>
                <input
                  id="wa-name"
                  type="text"
                  className="whatsapp-assistant-input"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  required
                />
                <div className="whatsapp-assistant-nav">
                  <button
                    type="button"
                    className="whatsapp-assistant-btn whatsapp-assistant-btn-secondary"
                    onClick={() => setStep(STEPS.GUEST_COUNT)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="whatsapp-assistant-btn whatsapp-assistant-btn-primary"
                    disabled={!form.name.trim()}
                    onClick={() => setStep(STEPS.PHONE)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {step === STEPS.PHONE && (
              <div className="whatsapp-assistant-step">
                <label className="whatsapp-assistant-label" htmlFor="wa-phone">
                  Your phone number
                </label>
                <input
                  id="wa-phone"
                  type="tel"
                  className="whatsapp-assistant-input"
                  placeholder="+91 98765 43210"
                  value={form.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  required
                />
                <div className="whatsapp-assistant-nav">
                  <button
                    type="button"
                    className="whatsapp-assistant-btn whatsapp-assistant-btn-secondary"
                    onClick={() => setStep(STEPS.NAME)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="whatsapp-assistant-btn whatsapp-assistant-btn-primary"
                    disabled={!form.phone.trim()}
                    onClick={() => setStep(STEPS.DETAILS)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {step === STEPS.DETAILS && (
              <div className="whatsapp-assistant-step">
                <label className="whatsapp-assistant-label" htmlFor="wa-details">
                  Tell us more <span className="wa-optional">(optional)</span>
                </label>
                <textarea
                  id="wa-details"
                  className="whatsapp-assistant-textarea"
                  rows={4}
                  placeholder="Venue, services needed, budget range…"
                  value={form.details}
                  onChange={(e) => updateField("details", e.target.value)}
                />
                <div className="whatsapp-assistant-nav">
                  <button
                    type="button"
                    className="whatsapp-assistant-btn whatsapp-assistant-btn-secondary"
                    onClick={() => setStep(STEPS.PHONE)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="whatsapp-assistant-btn whatsapp-assistant-btn-primary"
                    onClick={() => setStep(STEPS.SUMMARY)}
                  >
                    Review
                  </button>
                </div>
              </div>
            )}

            {step === STEPS.SUMMARY && (
              <div className="whatsapp-assistant-step">
                <p className="whatsapp-assistant-question">Review your inquiry</p>
                <pre className="whatsapp-assistant-summary">
                  {formatInquiryMessage(form)}
                </pre>
                <button
                  type="button"
                  className="whatsapp-assistant-btn whatsapp-assistant-btn-whatsapp"
                  onClick={handleSubmitInquiry}
                >
                  <FaWhatsapp aria-hidden />
                  Continue on WhatsApp
                </button>
                <button
                  type="button"
                  className="whatsapp-assistant-link"
                  onClick={() => setStep(STEPS.EVENT_TYPE)}
                >
                  Edit answers
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className="whatsapp-assistant-fab"
        onClick={open ? close : openPanel}
        aria-label={open ? "Close WhatsApp chat" : "Open WhatsApp chat"}
        aria-expanded={open}
      >
        {open ? <FaTimes aria-hidden /> : <FaWhatsapp aria-hidden />}
      </button>
    </div>
  );
};

export default WhatsAppAssistant;
